import React, { useEffect, useState } from 'react';
import { prepaidApi, driverApi } from '../services/api';
import { PrepaidRecord, CreatePrepaidRecord, Driver } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';

const PrepaidPage: React.FC = () => {
  const { t } = useLanguage();
  const [records, setRecords] = useState<PrepaidRecord[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PrepaidRecord | null>(null);
  const [linkFilter, setLinkFilter] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [formData, setFormData] = useState<CreatePrepaidRecord>({
    driverId: 0,
    prepaidDate: '',
    amount: 0,
    description: '',
    notes: ''
  });

  const fetchRecords = async () => {
    try {
      const response = await prepaidApi.getAll();
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching prepaid records:', error);
      alert('Error fetching prepaid records');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await driverApi.getAll();
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      alert('Error fetching drivers');
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchDrivers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingRecord) {
        await prepaidApi.update(editingRecord.id, formData);
        alert('Prepaid record updated successfully');
      } else {
        await prepaidApi.create(formData);
        alert('Prepaid record created successfully');
      }
      
      resetForm();
      fetchRecords();
    } catch (error) {
      console.error('Error saving prepaid record:', error);
      alert('Error saving prepaid record');
    }
  };

  const handleEdit = (record: PrepaidRecord) => {
    setEditingRecord(record);
    setFormData({
      driverId: record.driverId,
      prepaidDate: record.prepaidDate.split('T')[0], // Format date for input
      amount: record.amount,
      description: record.description || '',
      notes: record.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this prepaid record?')) {
      try {
        await prepaidApi.delete(id);
        alert('Prepaid record deleted successfully');
        fetchRecords();
      } catch (error) {
        console.error('Error deleting prepaid record:', error);
        alert('Error deleting prepaid record');
      }
    }
  };

  const handleUnlink = async (id: number) => {
    if (window.confirm('Are you sure you want to unlink this prepaid record?')) {
      try {
        await prepaidApi.unlink(id);
        alert('Prepaid record unlinked successfully');
        fetchRecords();
      } catch (error) {
        console.error('Error unlinking prepaid record:', error);
        alert('Error unlinking prepaid record');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      driverId: 0,
      prepaidDate: '',
      amount: 0,
      description: '',
      notes: ''
    });
    setEditingRecord(null);
    setShowModal(false);
  };

  // Filter records based on link status
  const filteredRecords = records.filter(record => {
    if (linkFilter === 'linked') return record.shippingRecordId;
    if (linkFilter === 'unlinked') return !record.shippingRecordId;
    return true;
  });

  if (loading) {
    return <div className="container-fluid mt-4"><div className="spinner-border"></div></div>;
  }

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>{t('prepaid.records')}</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          {t('add.prepaid.record')}
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Filter by Link Status:</label>
            <select 
              className="form-select" 
              style={{ width: '200px' }}
              value={linkFilter}
              onChange={(e) => setLinkFilter(e.target.value as 'all' | 'linked' | 'unlinked')}
            >
              <option value="all">All Records</option>
              <option value="linked">Linked Records</option>
              <option value="unlinked">Unlinked Records</option>
            </select>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('driver')}</th>
                  <th>{t('prepaid.date')}</th>
                  <th>{t('amount')}</th>
                  <th>{t('description')}</th>
                  <th>{t('link.status')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(record => (
                  <tr key={record.id}>
                    <td>{record.driverName}</td>
                    <td>{new Date(record.prepaidDate).toLocaleDateString()}</td>
                    <td>{formatVietnamCurrency(record.amount)}</td>
                    <td>{record.description || '-'}</td>
                    <td>
                      {record.shippingRecordId ? (
                        <span className="badge bg-success">
                          Linked to {record.shippingRecordInfo}
                        </span>
                      ) : (
                        <span className="badge bg-secondary">Not Linked</span>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button 
                          className="btn btn-outline-primary" 
                          onClick={() => handleEdit(record)}
                        >
                          {t('edit')}
                        </button>
                        {record.shippingRecordId && (
                          <button 
                            className="btn btn-outline-warning" 
                            onClick={() => handleUnlink(record.id)}
                          >
                            {t('unlink')}
                          </button>
                        )}
                        <button 
                          className="btn btn-outline-danger" 
                          onClick={() => handleDelete(record.id)}
                        >
                          {t('delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" style={{ minWidth: '140px', maxWidth: '40%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRecord ? t('edit.prepaid.record') : t('add.prepaid.record')}
                </h5>
                <button type="button" className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3 d-flex align-items-center">
                    <label className="form-label me-3 mb-0" style={{ minWidth: '140px' }}>{t('driver')} *</label>
                    <select
                      className="form-select"
                      value={formData.driverId}
                      onChange={(e) => setFormData({...formData, driverId: parseInt(e.target.value)})}
                      required
                    >
                      <option value={0}>{t('select.driver')}</option>
                      {drivers.map(driver => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name} - {driver.phone}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3 d-flex align-items-center">
                    <label className="form-label me-3 mb-0" style={{ minWidth: '140px' }}>{t('prepaid.date')} *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.prepaidDate}
                      onChange={(e) => setFormData({...formData, prepaidDate: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3 d-flex align-items-center">
                    <label className="form-label me-3 mb-0" style={{ minWidth: '140px' }}>{t('amount')} *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formatVietnamCurrency(formData.amount)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d]/g, '');
                        setFormData({...formData, amount: parseFloat(raw) || 0});
                      }}
                      required
                    />
                  </div>

                  <div className="mb-3 d-flex align-items-center">
                    <label className="form-label me-3 mb-0" style={{ minWidth: '140px' }}>{t('description')}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      maxLength={500}
                    />
                  </div>

                  <div className="mb-3 d-flex align-items-center">
                    <label className="form-label me-3 mb-0" style={{ minWidth: '140px' }}>{t('notes')}</label>
                    <textarea
                      className="form-control"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={3}
                      maxLength={1000}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    {t('cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingRecord ? t('update') : t('create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrepaidPage;